import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import type { ItemType } from "antd/es/menu/hooks/useItems";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { Role } from "@mapmycustomers/shared";
import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { Activity, Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";
import { Button, useModal } from "@mapmycustomers/ui";

import {
  createActivity,
  showGlobalCreateActivityModal,
} from "@app/component/createEditEntity/Activity/store/actions";
import { getActiveActivityTypes } from "@app/store/activity";
import { checkEmailSupporting, showEmailCreationModal } from "@app/store/email/actions";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import sendEmailViaClient from "@app/util/email/sendEmailViaClient";
import useEmailSendVariants from "@app/util/email/useEmailSendVariants";
import { activityLayoutModel } from "@app/util/layout/impl";
import loggingService from "@app/util/logging";

import styles from "./Contacts.module.scss";

interface Props {
  activityTypes: ActivityType[];
  checkEmailSupporting: typeof checkEmailSupporting;
  company?: Company;
  currentUser: User;
  email?: string;
  onCreateActivity: typeof createActivity.request;
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal;
  onShowEmailCreationModal: typeof showEmailCreationModal;
  person?: Person;
}

const EmailButton: React.FC<Props> = ({
  activityTypes,
  checkEmailSupporting,
  company,
  currentUser,
  email,
  onCreateActivity,
  onShowCreateActivityModal,
  onShowEmailCreationModal,
  person,
}) => {
  const analyticService = useAnalytics();
  const intl = useIntl();
  const entity = company ?? person;
  const emailValue = entity?.email ?? email;

  const [modal, contextHolder] = useModal();

  const isEmailActivityTypeActive = useMemo(
    () =>
      activityTypes.some(({ active, name }) => name === DefaultActivityTypeName.EMAIL && active),
    [activityTypes]
  );

  if (!company && !person && !email) {
    loggingService.warning(
      "EmailButton should have either company or person or email prop defined"
    );
  }

  const emailActivityType = useMemo(
    () => activityTypes.find((activity) => activity.name === DefaultActivityTypeName.EMAIL),
    [activityTypes]
  );

  const activity: OnlyRequiredFields<
    Omit<Activity, "files">,
    "crmActivityType" | "entity"
  > = useMemo(() => {
    const date = new Date();
    return {
      account: company ?? undefined,
      assignee: currentUser,
      assigneeId: currentUser.id,
      completed: true,
      contact: person ?? undefined,
      crmActivityType: emailActivityType!,
      entity: EntityType.ACTIVITY,
      name: `${DefaultActivityTypeName.EMAIL} ${entity?.name ?? ""}`,
      startAt: date.toISOString(),
    };
  }, [company, currentUser, entity, emailActivityType, person]);

  const handleLogActivity = useCallback(() => {
    // this shouldn't really happen, but still being cautious
    if (!emailActivityType) {
      onShowCreateActivityModal({ activity });
      return;
    }

    const hasRequiredFields = activityLayoutModel.hasMissingRequiredFieldsFor(activity);
    if (hasRequiredFields) {
      onShowCreateActivityModal({ activity });
    } else {
      onCreateActivity({
        activity,
        customFieldsValues: [],
        layoutId: activityLayoutModel.defaultLayout.id,
      });
    }
  }, [activity, emailActivityType, onCreateActivity, onShowCreateActivityModal]);

  const handleEmailClick = useCallback(() => {
    analyticService?.clicked(["Email from email client"], { email: emailValue });
    sendEmailViaClient(
      email ?? "",
      intl,
      company?.name ?? person?.name ?? "",
      isEmailActivityTypeActive ? modal : undefined,
      handleLogActivity
    );
  }, [
    analyticService,
    emailValue,
    email,
    intl,
    company?.name,
    person?.name,
    isEmailActivityTypeActive,
    modal,
    handleLogActivity,
  ]);

  const isDisabled = !emailValue;

  const handleSendEmailViaMMC = useCallback(() => {
    if (entity) {
      analyticService?.clicked(["Email from MMC"], { email: emailValue });
      checkEmailSupporting({
        modal,
        successfulCallback: () => {
          onShowEmailCreationModal({ recipients: [entity], recipientType: entity.entity });
        },
      });
    }
  }, [analyticService, checkEmailSupporting, emailValue, entity, modal, onShowEmailCreationModal]);

  const menuItems: ItemType[] = useEmailSendVariants({
    email: emailValue ?? "",
    isReadOnlyMember: currentUser.role.key === Role.READ_ONLY,
    onSendViaEmailClient: handleEmailClick,
    onSendViaMmcEmail: handleSendEmailViaMMC,
  });

  return (
    <Tooltip
      title={
        isDisabled
          ? intl.formatMessage({
              id: "entityPreview.contacts.noEmail",
              defaultMessage: "No email",
              description: "Tooltip for the Email button in Contacts section in the Preview pane",
            })
          : emailValue
      }
    >
      {
        <Dropdown disabled={isDisabled} menu={{ items: menuItems }}>
          <Button className={styles.textFieldButton} disabled={isDisabled} size="small" type="link">
            <div className={styles.textFieldIcon}>
              <FontAwesomeIcon className={styles.iconColor} icon={faEnvelope} />
            </div>
          </Button>
        </Dropdown>
      }
      {contextHolder}
    </Tooltip>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActiveActivityTypes(state),
  currentUser: getCurrentUser(state)!,
});

const mapDispatchToProps = {
  checkEmailSupporting,
  onCreateActivity: createActivity.request,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
  onShowEmailCreationModal: showEmailCreationModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailButton);
